import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import clsx from "clsx";

export interface DateRange {
  from: string | null; // ISO yyyy-mm-dd
  to: string | null;
}

interface Props {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const PRESETS: { key: string; label: string; days: number | null }[] = [
  { key: "all", label: "All time", days: null },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Date filter: quick presets (7/30/90 days) plus an optional custom from–to range. */
export default function DateRangeSelect({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const active = value.from !== null || value.to !== null;

  const presetMatch = (days: number | null): boolean =>
    days === null ? !active : value.from === isoDaysAgo(days) && value.to === null;

  const label = (() => {
    if (!active) return "All time";
    const preset = PRESETS.find((p) => p.days !== null && presetMatch(p.days));
    if (preset) return preset.label;
    return `${value.from ?? "…"} → ${value.to ?? "…"}`;
  })();

  const applyPreset = (days: number | null) => {
    onChange(days === null ? { from: null, to: null } : { from: isoDaysAgo(days), to: null });
    setCustom(false);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm transition",
          active
            ? "bg-fg/[0.10] text-mut-100 border-fg/[0.18]"
            : "bg-transparent text-mut-300 border-fg/[0.08] hover:bg-fg/[0.04] hover:text-mut-100",
        )}
      >
        <span className="text-mut-300">Date:</span>
        <span className={active ? "text-mut-100" : "text-mut-300"}>{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-mut-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 min-w-[220px] panel p-1.5">
          {PRESETS.map((p) => {
            const isSel = !custom && presetMatch(p.days);
            return (
              <button
                key={p.key}
                type="button"
                className="cmdk-item w-full"
                data-selected={isSel ? "true" : "false"}
                onClick={() => applyPreset(p.days)}
              >
                <span className="flex-1">{p.label}</span>
                {isSel && <Check className="w-3.5 h-3.5 text-accent-azure" />}
              </button>
            );
          })}
          <button
            type="button"
            className="cmdk-item w-full"
            data-selected={custom ? "true" : "false"}
            onClick={() => setCustom((c) => !c)}
          >
            <span className="flex-1">Custom…</span>
          </button>
          {custom && (
            <div className="flex flex-col gap-1.5 px-2 py-2">
              <label className="text-xs text-mut-300">
                From
                <input
                  type="date"
                  className="input mt-1"
                  value={value.from ?? ""}
                  onChange={(e) => onChange({ ...value, from: e.target.value || null })}
                />
              </label>
              <label className="text-xs text-mut-300">
                To
                <input
                  type="date"
                  className="input mt-1"
                  value={value.to ?? ""}
                  onChange={(e) => onChange({ ...value, to: e.target.value || null })}
                />
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
